import {
    ArrayField,
    ArrayFields,
    ConfigurationOptions,
    CRMConfigurationEntry,
    CRMFormData,
    CRMServerRequest,
    CRMServerResponse,
    JSONData,
    KeyedCrmConfigEntries,
    TemplateField
} from '../Types/Types'
import { EMPTY_STRING } from '../../../../../../React/Common/Constants/Separators'
import { IsStringEmpty, isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { JSONResponse } from '../../../../../../NonReact/common/Types/Types'
import { SessionAPIResponse } from '../../../../../../NonReact/Types/Types'
import { FormTypes } from '../../../../Molecules/ConfigurableTableFieldForm/Types/Types'
import { encrypt } from '../../../../../../React/Common/Utils/Security'
import { DEFAULT_PASSWORD_VALUE } from '../../../../../../NonReact/constants/Common'
import { CRMConfigurationTemplateType } from '../Constants/Constants'

export const ConfigSchemaPrefix: string = 'ConfigSchema.'
export const CommonPrefix: string = 'Common.'

const PREFIXES: string[] = [ConfigSchemaPrefix, CommonPrefix]

const removePrefix = (item: string, prefixs: string[]): string =>
    prefixs.reduce((acc: string, prefix: string): string => {
        if (acc.startsWith(prefix)) {
            return acc.slice(prefix.length)
        }
        return acc
    }, item)

const getStringifiedConfiguration = (
    data: CRMConfigurationEntry[],
    startsWith: string,
    crmTemplates: TemplateField[],
    Nonce: Nullable<string>
): JSONData<string> => ({
    JSONData: JSON.stringify(
        isVarNotEmpty(data) && data.length > 0
            ? data
                  .filter(
                      (entry: CRMConfigurationEntry): boolean =>
                          entry.item.startsWith(startsWith) && entry.value !== DEFAULT_PASSWORD_VALUE
                  )
                  .reduce((acc: ConfigurationOptions, entry: CRMConfigurationEntry): ConfigurationOptions => {
                      const templateField: TemplateField | undefined = crmTemplates.find(
                          (template: TemplateField): boolean => template.name === entry.item
                      )

                      let newEntry: ConfigurationOptions

                      switch (templateField?.type) {
                          case FormTypes.PASSWORD:
                              if (IsStringEmpty(entry.value.toString())) {
                                  return acc
                              }

                              newEntry = {
                                  [removePrefix(entry.item, PREFIXES)]: encrypt(entry.value.toString(), Nonce)
                              }
                              break
                          case FormTypes.ARRAY:
                              newEntry = {
                                  [removePrefix(entry.item, PREFIXES)]: (entry.value as ArrayFields).map(
                                      (fieldArrayItem: ArrayField): string => fieldArrayItem.value
                                  )
                              }
                              break
                          default:
                              newEntry = {
                                  [removePrefix(entry.item, PREFIXES)]: entry.value as
                                      | string
                                      | number
                                      | boolean
                                      | string[]
                              }
                      }

                      return {
                          ...acc,
                          ...newEntry
                      }
                  }, {})
            : {}
    )
})

export const mapFromStateToServer = (
    state: CRMFormData,
    isUpdating: boolean,
    crmTemplates: TemplateField[],
    session: JSONResponse<SessionAPIResponse> | null = null
): CRMServerRequest => ({
    Description: state?.details?.description ?? EMPTY_STRING,
    CRMType: isUpdating ? state.configuration?.crmType : undefined,
    ConfigurationType: state.configuration.templateType ?? CRMConfigurationTemplateType.Desktop,
    CRMConfiguration: getStringifiedConfiguration(
        state?.configuration?.crmOptions ?? [],
        ConfigSchemaPrefix,
        crmTemplates,
        session?.Nonce ?? null
    ),
    CRMConfigurationCommon: getStringifiedConfiguration(
        state?.configuration?.crmOptions ?? [],
        CommonPrefix,
        crmTemplates,
        session?.Nonce ?? null
    ),
    ...(isVarNotEmpty(state?.configuration?.oauthSessionID)
        ? { OAuthSessionID: state.configuration.oauthSessionID }
        : {})
})

export const mapConfigToFormValue = (
    config: Record<string, string | number | boolean>,
    prefix: string
): CRMConfigurationEntry[] =>
    Object.entries(config).map(
        ([key, value]: [string, string | number | boolean | string[]]): CRMConfigurationEntry => ({
            item: `${prefix ?? EMPTY_STRING}${key}`,
            value:
                isVarNotEmpty(value) && Array.isArray(value)
                    ? value.map(
                          (entry: string): ArrayField => ({
                              value: entry
                          })
                      )
                    : value ?? EMPTY_STRING
        })
    )

export const mapConfigValueEntries = (
    config: Record<string, string | number | boolean>,
    prefix: string
): KeyedCrmConfigEntries =>
    Object.fromEntries(
        Object.entries(config).map(([key, value]) => [
            `${prefix}${key}`,
            isVarNotEmpty(value) && Array.isArray(value)
                ? value.map((entry: string): { value: string } => ({
                      value: entry
                  }))
                : value ?? EMPTY_STRING
        ])
    )

export const mapFromServerToForm = (response: CRMServerResponse): CRMFormData => {
    const options: CRMFormData = {
        details: {
            description: response.Description,
            ID: response.CRMConfigurationID
        },
        scopeAssignment: {
            cme: response.CMEnvironmentID,
            telephonyServer: Number(response.TelSysID),
            partition: Number(response.PartIDInTelSys),
            groupDevice: response.DeviceNumber
        },
        configuration: {
            crmType: response.CRMType,
            templateType: response.ConfigurationType,
            crmOptions: []
        }
    }

    return options
}
